import { ValidationIssue } from './templateSaveValidation';
import { highlightElement } from '../../../../hooks/useHighlightSection';
import { POSTER_PULSE_KEYFRAMES, POSTER_PULSE_OPTIONS } from './posterPulse';

export const SECTION_PARAM = 'section';
export const DEVICE_TAB_PARAM = 'deviceTab';

// The template loads async, so poll for the section rather than assuming it is
// mounted by the time we arrive.
const POLL_MS = 100;
const MAX_ATTEMPTS = 50;
const SETTLE_MS = 300;

const SCROLL_OFFSET_TOP = 80;

const DEVICE_TABS = ['apple', 'google', 'shared'] as const;

type DeviceTab = ValidationIssue['deviceTab'];

export type SectionFocus = {
  sectionId: string;
  deviceTab: DeviceTab;
};

/**
 * Where to send someone who is looking at a validation issue from outside the
 * builder. The builder reads these back on arrival and opens the section.
 */
export const sectionFocusUrl = (
  baseUrl: string,
  issue: ValidationIssue
): string => {
  if (!issue.sectionId) return baseUrl;

  const params = new URLSearchParams({ [SECTION_PARAM]: issue.sectionId });
  if (issue.deviceTab) params.set(DEVICE_TAB_PARAM, issue.deviceTab);

  return `${baseUrl}?${params}`;
};

export const readSectionFocus = (
  search: URLSearchParams
): SectionFocus | null => {
  const sectionId = search.get(SECTION_PARAM);
  if (!sectionId) return null;

  const deviceTab = search.get(DEVICE_TAB_PARAM);

  return {
    sectionId,
    deviceTab: DEVICE_TABS.find((tab) => tab === deviceTab),
  };
};

export const POSTER_RING_HIGHLIGHT = {
  keyframes: POSTER_PULSE_KEYFRAMES,
  options: POSTER_PULSE_OPTIONS,
};

type SectionHighlight = typeof POSTER_RING_HIGHLIGHT;

export const pulseSection = (sectionId: string) =>
  highlightElement(document.getElementById(sectionId), {
    block: 'start',
    offsetTop: SCROLL_OFFSET_TOP,
    ...POSTER_RING_HIGHLIGHT,
  });

/**
 * Wait for a section to render, then scroll to it and pulse it. Returns a
 * cancel function for effect cleanup.
 *
 * `onDone` fires *after* the highlight, never before — a caller that clears its
 * pending state in `onDone` would otherwise tear this down mid-flight and the
 * highlight would never run.
 */
export const highlightSectionWhenReady = (
  sectionId: string,
  onDone: () => void,
  highlight?: SectionHighlight
): (() => void) => {
  let cancelled = false;
  let attempts = MAX_ATTEMPTS;

  const timer = setInterval(() => {
    if (cancelled) return;

    const el = document.getElementById(sectionId);
    if (el) {
      clearInterval(timer);
      // Let the tab switch settle before measuring the scroll position.
      setTimeout(() => {
        if (cancelled) return;
        highlightElement(el, {
          block: 'start',
          offsetTop: SCROLL_OFFSET_TOP,
          ...highlight,
        });
        onDone();
      }, SETTLE_MS);
      return;
    }

    if (--attempts <= 0) {
      clearInterval(timer);
      onDone();
    }
  }, POLL_MS);

  return () => {
    cancelled = true;
    clearInterval(timer);
  };
};
